import { useEffect, useState } from "react";
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useReorderSensors } from "@/hooks/useReorderSensors";
import { reorderIdsByDragEnd } from "@/lib/reorder";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Settings2 } from "lucide-react";
import { Button, Label, Modal, ModalFooter, ToggleGroup, Checkbox } from "@/components/ui";
import { cn } from "@/lib/utils";
import { PAGE_SIZE_OPTIONS } from "./tableConstants";
import type { DataTableLabels } from "./tableLabels";
import type { TablePreferences } from "./tablePreferencesStorage";
import type { TableColumnPreferenceMeta } from "./useTablePreferences";

interface TablePreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  labels: DataTableLabels;
  closeLabel: string;
  columns: TableColumnPreferenceMeta[];
  value: TablePreferences;
  onApply: (next: TablePreferences) => void;
  onReset: () => void;
  showPageSize?: boolean;
}

function SortableColumnRow({
  column,
  checked,
  locked,
  disableHide,
  onToggle,
  reorderLabel,
}: {
  column: TableColumnPreferenceMeta;
  checked: boolean;
  locked: boolean;
  disableHide: boolean;
  onToggle: (id: string, visible: boolean) => void;
  reorderLabel: string;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={cn(
        "flex items-center gap-2 rounded-xl border border-surface-200 bg-surface-0 px-3 py-2",
        isDragging && "z-10 shadow-card",
      )}
    >
      <button
        type="button"
        className="touch-none cursor-grab text-surface-400 active:cursor-grabbing"
        aria-label={reorderLabel}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <Label
        spacing="none"
        className="flex min-w-0 flex-1 items-center gap-2 text-surface-800"
      >
        <Checkbox
          checked={checked}
          disabled={locked || disableHide}
          onChange={(event) => onToggle(column.id, event.target.checked)}
        />
        <span className="min-w-0 break-words">{column.label}</span>
      </Label>
    </li>
  );
}

export function TablePreferencesModal({
  isOpen,
  onClose,
  labels,
  closeLabel,
  columns,
  value,
  onApply,
  onReset,
  showPageSize = true,
}: TablePreferencesModalProps) {
  const [draft, setDraft] = useState<TablePreferences>(value);

  useEffect(() => {
    if (isOpen) {
      setDraft(value);
    }
  }, [isOpen, value]);

  const sensors = useReorderSensors();

  const columnById = new Map(columns.map((column) => [column.id, column]));
  const orderedColumns = draft.columnOrder
    .map((id) => columnById.get(id))
    .filter((column): column is TableColumnPreferenceMeta => column != null);

  const visibleSet = new Set(draft.visibleColumnIds);

  const handleDragEnd = (event: DragEndEvent) => {
    const reordered = reorderIdsByDragEnd(draft.columnOrder, event);
    if (!reordered) {
      return;
    }
    setDraft((prev) => ({
      ...prev,
      columnOrder: reordered,
    }));
  };

  const handleToggle = (id: string, visible: boolean) => {
    setDraft((prev) => {
      const next = new Set(prev.visibleColumnIds);
      if (visible) {
        next.add(id);
      } else if (next.size > 1) {
        next.delete(id);
      }
      return { ...prev, visibleColumnIds: [...next] };
    });
  };

  const canConfirm = draft.visibleColumnIds.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeLabel={closeLabel}
      title={labels.preferencesTitle}
      description={labels.preferencesDescription}
      size="md"
      footer={
        <ModalFooter className="sm:justify-between">
          <Button
            variant="ghost"
            onClick={() => {
              onReset();
              onClose();
            }}
          >
            {labels.preferencesReset}
          </Button>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:gap-3">
            <Button variant="outline" onClick={onClose}>
              {labels.preferencesCancel}
            </Button>
            <Button
              disabled={!canConfirm}
              onClick={() => {
                onApply(draft);
                onClose();
              }}
            >
              {labels.preferencesConfirm}
            </Button>
          </div>
        </ModalFooter>
      }
    >
      <div className="flex flex-col gap-6">
        {showPageSize ? (
          <section>
            <h3 className="text-sm font-semibold text-surface-900">
              {labels.preferencesPageSize}
            </h3>
            <ToggleGroup
              className="mt-3"
              value={String(draft.pageSize)}
              onChange={(value) =>
                setDraft((prev) => ({
                  ...prev,
                  pageSize: Number(value),
                }))
              }
              options={PAGE_SIZE_OPTIONS.map((size) => ({
                value: String(size),
                label: String(size),
              }))}
              ariaLabel={labels.preferencesPageSize}
            />
          </section>
        ) : null}

        <section>
          <h3 className="text-sm font-semibold text-surface-900">
            {labels.preferencesColumns}
          </h3>
          <p className="mt-1 text-sm text-surface-500">
            {labels.preferencesColumnsHint}
          </p>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedColumns.map((column) => column.id)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="mt-3 flex flex-col gap-2">
                {orderedColumns.map((column) => (
                  <SortableColumnRow
                    key={column.id}
                    column={column}
                    checked={visibleSet.has(column.id)}
                    locked={!!column.alwaysVisible}
                    disableHide={
                      visibleSet.has(column.id) && visibleSet.size === 1
                    }
                    onToggle={handleToggle}
                    reorderLabel={labels.reorderColumn}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        </section>
      </div>
    </Modal>
  );
}

interface TablePreferencesButtonProps {
  labels: Pick<DataTableLabels, "preferencesOpen">;
  onClick: () => void;
  className?: string;
}

export function TablePreferencesButton({
  labels,
  onClick,
  className,
}: TablePreferencesButtonProps) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={onClick}
      className={className}
      aria-label={labels.preferencesOpen}
      leftIcon={<Settings2 className="h-4 w-4" />}
    >
      {/* Icon-only on phones so the header row keeps room for the title. */}
      <span className="hidden sm:inline">{labels.preferencesOpen}</span>
    </Button>
  );
}
